import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Server, Globe, Key, Settings, Copy, Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface ModelVendor {
  id: string;
  vendor_name: string;
  description?: string;
  is_openai_compatible: boolean;
  api_base_url?: string;
  model_list: any[];
  api_key?: string;
  headers?: any;
  rate_limit_config?: any;
  status: string;
  priority: number;
  is_default: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

interface FormData {
  vendor_name: string;
  description: string;
  is_openai_compatible: boolean;
  api_base_url: string;
  model_list: any[];
  api_key: string;
  headers: any;
  rate_limit_config: any;
  status: string;
  priority: number;
  is_default: boolean;
  metadata: any;
}

const ModelVendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<ModelVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [compatibilityFilter, setCompatibilityFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<ModelVendor | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    vendor_name: '',
    description: '',
    is_openai_compatible: false,
    api_base_url: '',
    model_list: [],
    api_key: '',
    headers: {},
    rate_limit_config: {},
    status: 'active',
    priority: 5,
    is_default: false,
    metadata: {}
  });

  const statusOptions = [
    { value: 'active', label: '活跃', color: 'green' },
    { value: 'inactive', label: '停用', color: 'gray' },
    { value: 'testing', label: '测试中', color: 'yellow' }
  ];

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=vendors&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setVendors(result.data.data || []);
      } else {
        console.error('获取厂商失败:', result.error);
        toast.error('获取厂商失败');
      }
    } catch (error) {
      console.error('获取厂商失败:', error);
      toast.error('获取厂商失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除厂商「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=vendors&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('厂商删除成功');
      await fetchVendors();
    } catch (error) {
      console.error('删除厂商失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleEdit = (vendor: ModelVendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendor_name: vendor.vendor_name,
      description: vendor.description || '',
      is_openai_compatible: vendor.is_openai_compatible,
      api_base_url: vendor.api_base_url || '',
      model_list: vendor.model_list || [],
      api_key: vendor.api_key || '',
      headers: vendor.headers || {},
      rate_limit_config: vendor.rate_limit_config || {},
      status: vendor.status,
      priority: vendor.priority,
      is_default: vendor.is_default,
      metadata: vendor.metadata || {}
    });
    setShowEditModal(true);
  };

  const handleCreate = () => {
    setFormData({
      vendor_name: '',
      description: '',
      is_openai_compatible: false,
      api_base_url: '',
      model_list: [],
      api_key: '',
      headers: {},
      rate_limit_config: {},
      status: 'active',
      priority: 5,
      is_default: false,
      metadata: {}
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (isEdit: boolean = false) => {
    try {
      const url = `${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=vendors&action=${isEdit ? 'update' : 'create'}`;
      const method = isEdit ? 'PUT' : 'POST';
      const data = isEdit ? { ...formData, id: editingVendor?.id } : formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(isEdit ? '厂商更新成功' : '厂商创建成功');
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingVendor(null);
      await fetchVendors();
    } catch (error) {
      console.error('操作失败:', error);
      toast.error('操作失败：' + (error as Error).message);
    }
  };

  const handleCopyApiKey = async (apiKey: string, id: string) => {
    if (!apiKey) {
      toast.error('该厂商未设置API密钥');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedId(id);
      toast.success('API密钥已复制到剪贴板');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('复制失败');
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (vendor.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
    const matchesCompatibility = compatibilityFilter === 'all' || 
                                (compatibilityFilter === 'openai' && vendor.is_openai_compatible) ||
                                (compatibilityFilter === 'custom' && !vendor.is_openai_compatible);
    return matchesSearch && matchesStatus && matchesCompatibility;
  });

  const getStatusInfo = (status: string) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Server className="w-6 h-6 mr-2 text-blue-600" />
              模型厂商管理
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加厂商
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索厂商名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              value={compatibilityFilter}
              onChange={(e) => setCompatibilityFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              <option value="openai">OpenAI兼容</option>
              <option value="custom">自定义接口</option>
            </select>
          </div>

          {/* 厂商列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredVendors.map((vendor) => {
              const statusInfo = getStatusInfo(vendor.status);
              
              return (
                <div key={vendor.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${vendor.is_openai_compatible ? 'bg-green-100' : 'bg-blue-100'}`}>
                        {vendor.is_openai_compatible ? (
                          <Globe className="w-5 h-5 text-green-600" />
                        ) : (
                          <Settings className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          {vendor.vendor_name}
                          {vendor.is_default && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              默认
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            vendor.is_openai_compatible 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {vendor.is_openai_compatible ? 'OpenAI兼容' : '自定义接口'}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {vendor.api_key && (
                        <button
                          onClick={() => handleCopyApiKey(vendor.api_key!, vendor.id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="复制API密钥"
                        >
                          {copiedId === vendor.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Key className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(vendor)}
                        className="text-blue-600 hover:text-blue-900"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(vendor.id, vendor.vendor_name)}
                        className="text-red-600 hover:text-red-900"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {vendor.description && (
                    <p className="text-gray-600 text-sm mb-3">{vendor.description}</p>
                  )}

                  {vendor.api_base_url && (
                    <div className="bg-gray-50 rounded-md p-3 mb-4">
                      <div className="text-xs text-gray-500 mb-1">API地址</div>
                      <div className="text-sm text-gray-700 font-mono break-all">
                        {vendor.api_base_url}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span>优先级: {vendor.priority}</span>
                    <span>模型数量: {vendor.model_list?.length || 0}</span>
                  </div>

                  {vendor.model_list && vendor.model_list.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 mb-2">支持的模型</div>
                      <div className="flex flex-wrap gap-1">
                        {vendor.model_list.slice(0, 3).map((model: any, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {model.display_name || model.name}
                          </span>
                        ))}
                        {vendor.model_list.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            +{vendor.model_list.length - 3} 更多
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>创建: {new Date(vendor.created_at).toLocaleDateString()}</span>
                    <span>更新: {new Date(vendor.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredVendors.length === 0 && (
            <div className="text-center py-12">
              <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无厂商数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 创建/编辑模态框 */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {showEditModal ? '编辑厂商' : '添加厂商'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  厂商名称 *
                </label>
                <input
                  type="text"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入厂商名称"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="请输入厂商描述"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_openai_compatible"
                  checked={formData.is_openai_compatible}
                  onChange={(e) => setFormData({...formData, is_openai_compatible: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_openai_compatible" className="ml-2 block text-sm text-gray-900">
                  兼容OpenAI接口格式
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API基础URL
                </label>
                <input
                  type="url"
                  value={formData.api_base_url}
                  onChange={(e) => setFormData({...formData, api_base_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://api.example.com/v1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API密钥
                </label>
                <input
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入API密钥"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    状态
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    优先级
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 5})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                  设为默认厂商
                </label>
              </div>

              {/* 支持的模型列表 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  支持的模型列表
                </label>
                <ModelListEditor
                  models={formData.model_list}
                  onChange={(models) => setFormData({...formData, model_list: models})}
                />
              </div>

              {/* 高级配置 */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">高级配置</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      请求头配置 (JSON格式)
                    </label>
                    <textarea
                      value={JSON.stringify(formData.headers, null, 2)}
                      onChange={(e) => {
                        try {
                          const headers = JSON.parse(e.target.value);
                          setFormData({...formData, headers});
                        } catch (error) {
                          // 保持原值，不更新状态
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      rows={3}
                      placeholder='{"Authorization": "Bearer your-token"}'
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      速率限制配置 (JSON格式)
                    </label>
                    <textarea
                      value={JSON.stringify(formData.rate_limit_config, null, 2)}
                      onChange={(e) => {
                        try {
                          const rate_limit_config = JSON.parse(e.target.value);
                          setFormData({...formData, rate_limit_config});
                        } catch (error) {
                          // 保持原值，不更新状态
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      rows={3}
                      placeholder='{"requests_per_minute": 60, "tokens_per_minute": 40000}'
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      元数据 (JSON格式)
                    </label>
                    <textarea
                      value={JSON.stringify(formData.metadata, null, 2)}
                      onChange={(e) => {
                        try {
                          const metadata = JSON.parse(e.target.value);
                          setFormData({...formData, metadata});
                        } catch (error) {
                          // 保持原值，不更新状态
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      rows={3}
                      placeholder='{"region": "global", "official": true}'
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingVendor(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                取消
              </button>
              <button
                onClick={() => handleSubmit(showEditModal)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {showEditModal ? '更新' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 模型列表编辑器组件
interface ModelItem {
  name: string;
  display_name: string;
  type: string;
  max_tokens?: number;
  cost_per_1k_tokens?: {
    input: number;
    output: number;
  };
}

interface ModelListEditorProps {
  models: ModelItem[];
  onChange: (models: ModelItem[]) => void;
}

const ModelListEditor: React.FC<ModelListEditorProps> = ({ models, onChange }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newModel, setNewModel] = useState<ModelItem>({
    name: '',
    display_name: '',
    type: 'chat',
    max_tokens: 4096,
    cost_per_1k_tokens: { input: 0, output: 0 }
  });

  const modelTypes = [
    { value: 'chat', label: '对话模型' },
    { value: 'completion', label: '补全模型' },
    { value: 'embedding', label: '嵌入模型' },
    { value: 'multimodal', label: '多模态模型' }
  ];



  const updateModel = (index: number, updatedModel: ModelItem) => {
    const updatedModels = [...models];
    updatedModels[index] = updatedModel;
    onChange(updatedModels);
    setEditingIndex(null);
  };

  const removeModel = (index: number) => {
    const updatedModels = models.filter((_, i) => i !== index);
    onChange(updatedModels);
  };

  return (
    <div className="space-y-4">
      {/* 现有模型列表 */}
      <div className="space-y-2">
        {models.map((model, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
            {editingIndex === index ? (
              <ModelEditor
                model={model}
                onSave={(updatedModel) => updateModel(index, updatedModel)}
                onCancel={() => setEditingIndex(null)}
                modelTypes={modelTypes}
              />
            ) : (
              <>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{model.display_name}</span>
                    <span className="text-sm text-gray-500">({model.name})</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {modelTypes.find(t => t.value === model.type)?.label || model.type}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    最大令牌: {model.max_tokens || 'N/A'} | 
                    输入成本: ${model.cost_per_1k_tokens?.input || 0}/1K | 
                    输出成本: ${model.cost_per_1k_tokens?.output || 0}/1K
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingIndex(index)}
                    className="text-blue-600 hover:text-blue-900"
                    title="编辑"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeModel(index)}
                    className="text-red-600 hover:text-red-900"
                    title="删除"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* 添加新模型 */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">添加新模型</h4>
        <ModelEditor
          model={newModel}
          onSave={(model) => {
            const updatedModels = [...models, { ...model }];
            onChange(updatedModels);
            setNewModel({
              name: '',
              display_name: '',
              type: 'chat',
              max_tokens: 4096,
              cost_per_1k_tokens: { input: 0, output: 0 }
            });
          }}
          onCancel={() => setNewModel({
            name: '',
            display_name: '',
            type: 'chat',
            max_tokens: 4096,
            cost_per_1k_tokens: { input: 0, output: 0 }
          })}
          modelTypes={modelTypes}
          isNew={true}
        />
      </div>
    </div>
  );
};

// 模型编辑器组件
interface ModelEditorProps {
  model: ModelItem;
  onSave: (model: ModelItem) => void;
  onCancel: () => void;
  modelTypes: { value: string; label: string }[];
  isNew?: boolean;
}

const ModelEditor: React.FC<ModelEditorProps> = ({ model, onSave, onCancel, modelTypes, isNew = false }) => {
  const [editModel, setEditModel] = useState<ModelItem>({ ...model });

  const handleSave = () => {
    if (!editModel.name || !editModel.display_name) {
      toast.error('请填写模型名称和显示名称');
      return;
    }
    onSave(editModel);
  };

  return (
    <div className="space-y-3 w-full">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            模型名称 *
          </label>
          <input
            type="text"
            value={editModel.name}
            onChange={(e) => setEditModel({...editModel, name: e.target.value})}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="gpt-4"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            显示名称 *
          </label>
          <input
            type="text"
            value={editModel.display_name}
            onChange={(e) => setEditModel({...editModel, display_name: e.target.value})}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="GPT-4"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            模型类型
          </label>
          <select
            value={editModel.type}
            onChange={(e) => setEditModel({...editModel, type: e.target.value})}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {modelTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            最大令牌数
          </label>
          <input
            type="number"
            value={editModel.max_tokens || ''}
            onChange={(e) => setEditModel({...editModel, max_tokens: parseInt(e.target.value) || undefined})}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="4096"
          />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              输入成本
            </label>
            <input
              type="number"
              step="0.001"
              value={editModel.cost_per_1k_tokens?.input || ''}
              onChange={(e) => setEditModel({
                ...editModel, 
                cost_per_1k_tokens: {
                  ...editModel.cost_per_1k_tokens,
                  input: parseFloat(e.target.value) || 0,
                  output: editModel.cost_per_1k_tokens?.output || 0
                }
              })}
              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.03"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              输出成本
            </label>
            <input
              type="number"
              step="0.001"
              value={editModel.cost_per_1k_tokens?.output || ''}
              onChange={(e) => setEditModel({
                ...editModel, 
                cost_per_1k_tokens: {
                  input: editModel.cost_per_1k_tokens?.input || 0,
                  output: parseFloat(e.target.value) || 0
                }
              })}
              className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.06"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          onClick={onCancel}
          className="px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
        >
          取消
        </button>
        <button
          onClick={handleSave}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {isNew ? '添加' : '保存'}
        </button>
      </div>
    </div>
  );
};

export default ModelVendorManagement;